// frontend/src/components/LOS/Caprion/CaprionAAConsent.tsx
//
// Caprion Account Aggregator (AA) Consent — Ignosis FIU
// ======================================================
//
// Flow:
//  1. User sees consent page with checkbox + instructions about AA
//  2. User ticks the consent checkbox
//  3. Checkbox onChange immediately calls POST /api/caprion/aa/initiate/
//     → Backend calls Ignosis API 1: POST /web/multi-consent/initiate/phone-number
//     → Returns { redirect_url, session_pk, tracking_id, underwriting_reference_id }
//  4. redirect_url is opened in the same tab (window.location.href) so the user
//     completes the Ignosis AA journey (select bank → approve consent)
//  5. After the AA journey Ignosis redirects back to settings.IGNOSIS_REDIRECTION_URL
//     (e.g. https://www.google.co.in/) — the user then returns to the app
//  6. onDone() is called → journey advances to CaprionAAStatus step

import React, { useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  isBuilderMode?: boolean;
}

type Phase = "idle" | "calling" | "awaiting_user" | "error";

const CaprionAAConsent: React.FC<Props> = ({ onDone, isBuilderMode = false }) => {
  const calledRef = useRef(false);

  const [phase, setPhase]         = useState<Phase>("idle");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [aaUrl, setAaUrl]         = useState<string | null>(null);
  const [consented, setConsented] = useState(false);

  // ── Initiate AA (called when checkbox is ticked) ─────────────────────────
  const initiateAA = async () => {
    if (calledRef.current) return;
    calledRef.current = true;
    setPhase("calling");
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/caprion/aa/initiate/`, {});

      if (res.data?.success === false) {
        throw new Error(res.data?.error || "AA initiation failed.");
      }

      const { redirect_url, session_pk, underwriting_reference_id } = res.data || {};

      // Persist session identifiers for the status polling step
      if (session_pk) {
        sessionStorage.setItem("caprion_aa_session_pk", String(session_pk));
      }
      if (underwriting_reference_id) {
        sessionStorage.setItem("caprion_aa_underwriting_ref", underwriting_reference_id);
      }

      if (redirect_url) {
        setAaUrl(redirect_url);
        setPhase("awaiting_user");
        // Redirect user to Ignosis AA journey in the same tab
        window.location.href = redirect_url;
      } else {
        // No redirect URL (dev/mock mode) — advance directly
        onDone?.();
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[CaprionAAConsent] Error:", msg, err?.response?.data);
      setError(msg);
      setPhase("error");
      calledRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // ── Checkbox change handler ───────────────────────────────────────────────
  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setConsented(checked);
    if (error) setError(null);
    if (checked) {
      // Immediately trigger AA initiation when user ticks the checkbox
      initiateAA();
    }
  };

  // ── User confirms they completed the AA journey ───────────────────────────
  const handleAADone = () => {
    onDone?.();
  };

  // ── Builder mode preview ──────────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏦</div>
        <p className="font-semibold text-gray-500">Caprion: AA Consent (Ignosis)</p>
        <p className="text-xs mt-1">
          Shows consent checkbox. On tick, calls Ignosis multi-consent initiate API and
          redirects user to the AA journey URL. Stores all 3 consent referenceIds in DB.
        </p>
      </div>
    );
  }

  // ── Calling API ───────────────────────────────────────────────────────────
  if (phase === "calling") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Setting Up Bank Data Fetch…</h2>
            <p className="text-gray-500 text-sm">
              Preparing your Account Aggregator session with Ignosis…
            </p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
            <p className="text-xs text-gray-400">You will be redirected to the AA journey shortly…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Awaiting user (shown briefly before redirect, or if redirect fails) ───
  if (phase === "awaiting_user") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Redirecting to AA Journey…</h2>
              <p className="text-sm text-gray-500">
                You are being redirected to the Ignosis Account Aggregator to approve bank
                data sharing. If the redirect did not happen automatically, click below.
              </p>
            </div>

            {aaUrl && (
              <a
                href={aaUrl}
                className="block w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg text-center hover:bg-intellend-green/90 transition-colors"
              >
                Open AA Journey →
              </a>
            )}

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-700 text-center">
                After completing the AA journey, return to this page and click below.
              </p>
            </div>

            <button
              onClick={handleAADone}
              className="w-full py-3 px-6 border border-intellend-green text-intellend-green font-semibold rounded-lg hover:bg-intellend-green/5 transition-colors"
            >
              ✅ I've Completed the AA Journey — Continue
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">AA Initiation Failed</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please try again or contact support.</p>
            </div>
            <button
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
              onClick={() => {
                calledRef.current = false;
                setError(null);
                setAaUrl(null);
                setConsented(false);
                setPhase("idle");
              }}
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ── Default: idle — consent checkbox + instructions ───────────────────────
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">

          {/* Icon + Title */}
          <div className="text-center space-y-2">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Bank Statement Fetch via AA</h2>
            <p className="text-sm text-gray-500">
              We need to fetch your bank statements to process your loan application faster.
              This is done via the RBI-regulated Account Aggregator (AA) framework — your data
              is shared only with your explicit consent.
            </p>
          </div>

          {/* What happens */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-700">What will happen:</p>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Tick the checkbox below to give consent</li>
              <li>You will be redirected to the Ignosis AA portal</li>
              <li>Select your bank and approve the data-sharing consent</li>
              <li>Return here — we'll process your application</li>
            </ol>
          </div>

          {/* Consent checkbox — triggers AA initiation on tick */}
          <label className={`flex items-start gap-3 cursor-pointer ${loading ? "opacity-60 pointer-events-none" : ""}`}>
            <input
              type="checkbox"
              checked={consented}
              onChange={handleConsentChange}
              disabled={loading}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-intellend-green focus:ring-intellend-green"
            />
            <span className="text-sm text-gray-700">
              I consent to sharing my bank statement data via the Account Aggregator (AA) framework
              for the purpose of loan processing. I understand this is an RBI-regulated process and
              my data will be used only for credit assessment.{" "}
              <span className="text-intellend-green font-medium">
                (Ticking this will redirect you to the AA portal)
              </span>
            </span>
          </label>

          {loading && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Setting up AA session…
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              ⚠️ {error}
            </p>
          )}

          <p className="text-sm text-center font-medium text-gray-700">
            🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
          </p>
        </div>
      </main>
    </div>
  );
};

export default CaprionAAConsent;
